import React, { useState, useEffect, useCallback, useMemo } from "react";
import BackButton from "../../shared/BackButton/BackButton";
import LanguageControlsGrid, { AVAILABLE_LANGUAGES, PROFICIENCY_LEVELS, DIFFICULTIES, DOMAINS } from "../../shared/LanguageControlsGrid/LanguageControlsGrid";
import styles from "./FillInTheBlank.module.css";

// --- API Imports ---
import { fetchVocabularyWords } from "../../API/Vocabulary/VocabularyAPI";
import { fetchCourseOrderData } from "../../API/Course/CourseAPI";
import { callOpenAI } from "../../API/API";
import {
  fetchExistingPracticeSentences,
  createPracticeSentence,
  updatePracticeSentence,
  deletePracticeSentence,
} from "../../API/Lesson/FillInTheBlankAPI";

// --- GenAI Payload Import ---
import { buildFillInTheBlankGenAiPayload } from "../../GenAIPrompts/GenAIPrompts";

// --- Language Constants ---
const LANG_CODE_MAP: Record<string, string> = {
  English: "EN",
  Spanish: "ES",
  Italian: "IT",
  French: "FR",
  German: "DE",
  Portuguese: "PT",
};

const ALL_BASE_LANGUAGES = ["EN", "ES", "IT", "FR", "DE", "PT"];

// Extended domains for FillInTheBlank (shared component only has "general")
const EXTENDED_DOMAINS = [
  "general",
  "business",
  "travel",
  "technical",
  "medical",
  "food",
];

// --- Types ---
export interface Blank {
  correctAnswer: string;
  options: string[];
  placeholder?: string;
}

export interface FormattedPracticeSentence {
  sectionId: string;
  sentenceId: string;
  sentenceText: string;
  blanks: Blank[];
  createdAt: string;
  targetLanguage: string;
  baseLanguage: string;
  domain: string;
  proficiencyLevel: string;
  difficulty: string;
  _rawAiSentence?: string;
  _rawAiCorrectAnswer?: string;
  _rawAiOptions?: string[];
}

interface SentenceInput {
  sentence: string;
  correctWord: string;
}

interface RawAiQuestion {
  sentence: string;
  correctAnswer: string;
  options: string[];
}

// --- Helpers ---
function generateUUID() {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
}

const getCorrectAnswerFromBlanks = (blanks: Blank[]): string => blanks[0]?.correctAnswer || "N/A";

// Helper to render sentence with styled blanks
const renderSentenceWithBlanks = (sentenceText: string): React.ReactNode => {
  // Split by underscore patterns (consecutive underscores)
  const parts = sentenceText.split(/(_+)/g);

  return (
    <span>
      {parts.map((part, index) => {
        // Check if this part is a blank (contains only underscores)
        if (/^_+$/.test(part)) {
          return (
            <span key={index}>
              <span className={styles.blank}>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</span>{" "}
            </span>
          );
        }
        return <span key={index}>{part}</span>;
      })}
    </span>
  );
};

// --- Component ---
const FillInTheBlankManager: React.FC = () => {
  // State
  const [sourceLanguage, setSourceLanguage] = useState<string>("");
  const [baseLanguage, setBaseLanguage] = useState<string>("EN");
  const [section, setSection] = useState<string>("");
  const [domain, setDomain] = useState<string>("general");
  const [proficiencyLevel, setProficiencyLevel] = useState<string>("B1");
  const [difficulty, setDifficulty] = useState<string>("2");

  const [courseSections, setCourseSections] = useState<{
    identifier: string;
    sectionNameForAPI: string;
  }[]>([]);
  const [vocabularyWords, setVocabularyWords] = useState<string[]>([]);
  const [selectedWordsBucket, setSelectedWordsBucket] = useState<Set<string>>(new Set());
  const [existingPracticeSentences, setExistingPracticeSentences] =
    useState<FormattedPracticeSentence[]>([]);
  const [aiGeneratedContent, setAiGeneratedContent] =
    useState<FormattedPracticeSentence[] | null>(null);

  // Base language filter for existing exercises
  const [baseLanguageFilter, setBaseLanguageFilter] = useState<string>("All");

  // Selection state for bulk operations
  const [selectedSentenceTexts, setSelectedSentenceTexts] = useState<Set<string>>(new Set());

  // AI Edit Modal state (single edit)
  const [editingSentence, setEditingSentence] = useState<FormattedPracticeSentence | null>(null);
  const [showAIEditModal, setShowAIEditModal] = useState<boolean>(false);
  const [aiEditPrompt, setAIEditPrompt] = useState<string>("");
  const [aiEditPreview, setAIEditPreview] = useState<FormattedPracticeSentence | null>(null);
  const [isProcessingAIEdit, setIsProcessingAIEdit] = useState<boolean>(false);
  const [updateAllVariations, setUpdateAllVariations] = useState<boolean>(false);

  // Bulk AI Edit Modal state
  const [showBulkAIEditModal, setShowBulkAIEditModal] = useState<boolean>(false);
  const [bulkAIEditPrompt, setBulkAIEditPrompt] = useState<string>("");
  const [bulkAIEditPreviews, setBulkAIEditPreviews] = useState<Map<string, FormattedPracticeSentence>>(new Map());
  const [isProcessingBulkAIEdit, setIsProcessingBulkAIEdit] = useState<boolean>(false);

  // Generate Missing Base Languages state
  const [generatingMissingLanguages, setGeneratingMissingLanguages] = useState<Set<string>>(new Set());

  // loadingSections now handled by LanguageControlsGrid
  const [loadingExisting, setLoadingExisting] = useState<boolean>(false);
  const [generatingAi, setGeneratingAi] = useState<boolean>(false);
  const [isSavingAi, setIsSavingAi] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [aiError, setAiError] = useState<string | null>(null);

  const langCodeMap = useMemo(() => LANG_CODE_MAP, []);

  // Handle sections loaded from LanguageControlsGrid
  const handleSectionsLoaded = useCallback((sections: any[]) => {
    const processed = sections.map((item: any) => ({
      identifier: item.Identifier,
      sectionNameForAPI: item.Vocabulary_List,
    }));
    setCourseSections(processed);
  }, []);

  // Load vocabulary when section changes
  useEffect(() => {
    if (!section) return;
    setError(null);
    setVocabularyWords([]);
    setSelectedWordsBucket(new Set());
    fetchVocabularyWords(section)
      .then((ws) => {
        const uniq = Array.from(
          new Set(ws.map((w) => w.Targ_Word?.trim()).filter(Boolean))
        );
        setVocabularyWords(uniq.sort());
      })
      .catch((err) => setError(err.message || String(err)));
  }, [section]);

  // Get unique base languages from existing exercises
  const uniqueBaseLanguages = useMemo(() => {
    const languages = new Set(existingPracticeSentences.map(ex => ex.baseLanguage));
    return Array.from(languages).sort();
  }, [existingPracticeSentences]);

  // Filter exercises based on selected base language
  const filteredExercises = useMemo(() => {
    if (baseLanguageFilter === "All") {
      return existingPracticeSentences;
    }
    return existingPracticeSentences.filter(ex => ex.baseLanguage === baseLanguageFilter);
  }, [existingPracticeSentences, baseLanguageFilter]);

  // Group exercises by sentenceText
  interface GroupedSentence {
    sentenceText: string;
    targetLanguage: string;
    variations: FormattedPracticeSentence[];
  }

  const groupedSentences = useMemo(() => {
    const groups: Record<string, FormattedPracticeSentence[]> = {};

    filteredExercises.forEach(exercise => {
      const key = exercise.sentenceText;
      if (!groups[key]) {
        groups[key] = [];
      }
      groups[key].push(exercise);
    });

    return Object.entries(groups).map(([sentenceText, variations]) => ({
      sentenceText,
      targetLanguage: variations[0]?.targetLanguage || '',
      variations: variations.sort((a, b) => {
        // Prioritize EN, then alphabetical
        if (a.baseLanguage === 'EN') return -1;
        if (b.baseLanguage === 'EN') return 1;
        return a.baseLanguage.localeCompare(b.baseLanguage);
      })
    }));
  }, [filteredExercises]);

  // Load existing practice sentences
  useEffect(() => {
    if (!section) return;
    setLoadingExisting(true);
    setError(null);
    fetchExistingPracticeSentences(section)
      .then((items) => setExistingPracticeSentences(items))
      .catch((err) => setError(err.message || String(err)))
      .finally(() => setLoadingExisting(false));
  }, [section]);

  // Handlers
  const handleLanguageChange = (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    setSourceLanguage(e.target.value);
    setSection("");
  };
  const handleSectionChange = (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => setSection(e.target.value);
  const handleDomainChange = (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => setDomain(e.target.value);
  const handleProfChange = (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => setProficiencyLevel(e.target.value);
  const handleDiffChange = (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => setDifficulty(e.target.value);
  const toggleWord = (w: string) => {
    setSelectedWordsBucket((prev) => {
      const nxt = new Set(prev);
      nxt.has(w) ? nxt.delete(w) : nxt.add(w);
      return nxt;
    });
  };

  const toggleSentenceSelection = (sentenceText: string) => {
    setSelectedSentenceTexts((prev) => {
      const next = new Set(prev);
      next.has(sentenceText) ? next.delete(sentenceText) : next.add(sentenceText);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedSentenceTexts.size === groupedSentences.length) {
      setSelectedSentenceTexts(new Set());
    } else {
      setSelectedSentenceTexts(new Set(groupedSentences.map(g => g.sentenceText)));
    }
  };

  const handleGenerateData = async () => {
    if (!selectedWordsBucket.size) return;
    const inputs: SentenceInput[] = Array.from(
      selectedWordsBucket
    ).map((w) => ({
      sentence: `Context: ${section}, Word: ${w}`,
      correctWord: w,
    }));
    const payload = buildFillInTheBlankGenAiPayload(
      langCodeMap[sourceLanguage],
      inputs,
      AVAILABLE_LANGUAGES.map((l) => langCodeMap[l]).filter(Boolean) as string[],
      proficiencyLevel,
      difficulty,
      domain
    );
    setGeneratingAi(true);
    setAiError(null);
    try {
      const apiRes = await callOpenAI(payload);
      const raw = JSON.parse(apiRes?.response);
      if (!Array.isArray(raw)) throw new Error("AI response not array");
      const formatted = raw.map((q: RawAiQuestion) => ({
        sectionId: section,
        sentenceId: generateUUID(),
        sentenceText: q.sentence,
        blanks: [
          { correctAnswer: q.correctAnswer, options: q.options },
        ],
        createdAt: new Date().toISOString(),
        targetLanguage: langCodeMap[sourceLanguage],
        baseLanguage,
        domain,
        proficiencyLevel,
        difficulty,
      }));
      setAiGeneratedContent(formatted);
    } catch (err: any) {
      setAiError(err.message || String(err));
    } finally {
      setGeneratingAi(false);
    }
  };

  const handleSaveAll = async () => {
    if (!aiGeneratedContent) return;
    setIsSavingAi(true);
    await Promise.all(
      aiGeneratedContent.map((s) => createPracticeSentence(s))
    );
    setExistingPracticeSentences((prev) => [
      ...aiGeneratedContent!,
      ...prev,
    ]);
    setAiGeneratedContent(null);
    setIsSavingAi(false);
  };

  const handleDelete = async (sec: string, id: string) => {
    await deletePracticeSentence(sec, id);
    setExistingPracticeSentences((prev) =>
      prev.filter((x) => x.sentenceId !== id)
    );
  };

  const handleDeleteAll = async (sentenceText: string, sectionId: string) => {
    const confirmDelete = window.confirm(
      `Are you sure you want to delete ALL variations of this sentence?\n\nThis will delete all base language versions of:\n"${sentenceText}"\n\nThis action cannot be undone.`
    );

    if (!confirmDelete) return;

    try {
      // Find all variations with the same sentence text
      const variations = existingPracticeSentences.filter(
        (ex) => ex.sentenceText === sentenceText
      );

      // Delete all variations from the database
      await Promise.all(
        variations.map((variation) =>
          deletePracticeSentence(variation.sectionId, variation.sentenceId)
        )
      );

      // Update local state to remove all variations
      setExistingPracticeSentences((prev) =>
        prev.filter((sentence) => sentence.sentenceText !== sentenceText)
      );

      alert(`Successfully deleted ${variations.length} variation(s) of this sentence.`);
    } catch (err: any) {
      console.error("Error deleting all variations:", err);
      alert(`Failed to delete variations: ${err.message || "Unknown error"}`);
    }
  };

  // ── AI Editing Handlers (Single) ──
  const handleOpenAIEdit = (sentence: FormattedPracticeSentence) => {
    setEditingSentence(sentence);
    setShowAIEditModal(true);
    setAIEditPrompt("");
    setAIEditPreview(null);
    setUpdateAllVariations(false);
  };

  const handleCloseAIEdit = () => {
    setShowAIEditModal(false);
    setAIEditPrompt("");
    setAIEditPreview(null);
    setEditingSentence(null);
  };

  // ── Bulk AI Editing Handlers ──
  const handleOpenBulkAIEdit = () => {
    if (selectedSentenceTexts.size === 0) {
      alert("Please select at least one sentence to edit.");
      return;
    }
    setShowBulkAIEditModal(true);
    setBulkAIEditPrompt("");
    setBulkAIEditPreviews(new Map());
  };

  const handleCloseBulkAIEdit = () => {
    setShowBulkAIEditModal(false);
    setBulkAIEditPrompt("");
    setBulkAIEditPreviews(new Map());
  };

  const handleProcessBulkAIEdit = async () => {
    if (!bulkAIEditPrompt.trim()) {
      alert("Please enter modification instructions.");
      return;
    }

    setIsProcessingBulkAIEdit(true);
    const previews = new Map<string, FormattedPracticeSentence>();

    try {
      // Process each selected sentence
      for (const sentenceText of selectedSentenceTexts) {
        // Find the first variation (we'll use EN if available, otherwise first one)
        const variations = existingPracticeSentences.filter(
          (ex) => ex.sentenceText === sentenceText
        );
        const representativeSentence = variations.find(v => v.baseLanguage === 'EN') || variations[0];

        if (!representativeSentence) continue;

        // Build AI prompt for this sentence
        const systemContent = `
You are an AI assistant specialized in modifying fill-in-the-blank exercises for language learning.

CURRENT EXERCISE TO MODIFY:
Sentence: "${representativeSentence.sentenceText}"
Correct Answer: "${getCorrectAnswerFromBlanks(representativeSentence.blanks)}"
Answer Options: ${representativeSentence.blanks[0]?.options.join(", ")}
Target Language: ${representativeSentence.targetLanguage}

IMPORTANT: Your modification should preserve the exercise format while applying the user's requested changes.

RULES:
1. Modify the sentence based on the user's instructions
2. Provide a new correct answer (the word that fills the blank)
3. Provide 3-4 distractor options (incorrect words similar to the correct answer)
4. Ensure the sentence remains natural and grammatically correct
5. Keep the same target language
6. Maintain appropriate difficulty level

Output Format:
Respond ONLY with a valid JSON object:
{
  "sentence": "modified sentence with _____ blank",
  "correctAnswer": "correct word",
  "options": ["correct word", "distractor1", "distractor2", "distractor3"]
}`;

        const userContent = `
Target Language: ${representativeSentence.targetLanguage}
Domain: ${representativeSentence.domain}
Proficiency Level: ${representativeSentence.proficiencyLevel}
Difficulty: ${representativeSentence.difficulty}

Modification Instructions: ${bulkAIEditPrompt}

Please modify the exercise based on these instructions and return it in the exact JSON format specified.`;

        const payload = {
          messages: [
            { role: "system", content: systemContent },
            { role: "user", content: userContent },
          ],
          temperature: 0.7,
          max_tokens: 1000,
          json_mode: true,
        };

        const result = await callOpenAI(payload);

        if (!result) {
          console.error(`Failed to get AI response for: ${sentenceText}`);
          continue;
        }

        // Parse the response
        let parsedResponse: any;
        try {
          if (typeof result.response === 'object' && result.response !== null) {
            parsedResponse = result.response;
          } else if (typeof result.response === 'string') {
            let jsonString = result.response;
            const startIndex = jsonString.indexOf("{");
            const endIndex = jsonString.lastIndexOf("}") + 1;
            if (startIndex !== -1 && endIndex > startIndex) {
              jsonString = jsonString.slice(startIndex, endIndex).trim();
              parsedResponse = JSON.parse(jsonString);
            } else {
              throw new Error("No valid JSON found in response");
            }
          } else {
            throw new Error(`Unexpected response type: ${typeof result.response}`);
          }
        } catch (parseError: any) {
          console.error("Failed to parse AI response:", parseError);
          continue;
        }

        // Create preview
        const preview: FormattedPracticeSentence = {
          ...representativeSentence,
          sentenceText: parsedResponse.sentence,
          blanks: [{
            correctAnswer: parsedResponse.correctAnswer,
            options: parsedResponse.options || [parsedResponse.correctAnswer],
            placeholder: "___"
          }]
        };

        previews.set(sentenceText, preview);
      }

      setBulkAIEditPreviews(previews);

      if (previews.size === 0) {
        alert("Failed to process any sentences. Please check the console for errors.");
      }

    } catch (err: any) {
      console.error("Error processing bulk AI edit:", err);
      alert(`Failed to process bulk AI edit: ${err.message || "Unknown error"}`);
    } finally {
      setIsProcessingBulkAIEdit(false);
    }
  };

  const handleApplyBulkAIEdit = async () => {
    if (bulkAIEditPreviews.size === 0) return;

    try {
      // For each preview, update all variations of that sentence
      for (const [originalSentenceText, preview] of bulkAIEditPreviews) {
        const variations = existingPracticeSentences.filter(
          (ex) => ex.sentenceText === originalSentenceText
        );

        // Update each variation in the database
        await Promise.all(
          variations.map((variation) => {
            const updatedVariation: FormattedPracticeSentence = {
              ...variation,
              sentenceText: preview.sentenceText,
              blanks: preview.blanks,
            };
            return updatePracticeSentence(
              variation.sectionId,
              variation.sentenceId,
              updatedVariation
            );
          })
        );
      }

      // Update local state
      setExistingPracticeSentences((prev) =>
        prev.map((sentence) => {
          const preview = bulkAIEditPreviews.get(sentence.sentenceText);
          if (preview) {
            return {
              ...sentence,
              sentenceText: preview.sentenceText,
              blanks: preview.blanks,
            };
          }
          return sentence;
        })
      );

      handleCloseBulkAIEdit();
      setSelectedSentenceTexts(new Set());
      alert(`Successfully updated ${bulkAIEditPreviews.size} sentence(s) with AI changes!`);
    } catch (err: any) {
      console.error("Error applying bulk AI edit:", err);
      alert(`Failed to apply bulk AI edit: ${err.message || "Unknown error"}`);
    }
  };

  const handleProcessAIEdit = async () => {
    if (!aiEditPrompt.trim() || !editingSentence) {
      alert("Please enter modification instructions");
      return;
    }

    setIsProcessingAIEdit(true);
    try {
      // Build AI prompt for editing
      const systemContent = `
You are an AI assistant specialized in modifying fill-in-the-blank exercises for language learning.

CURRENT EXERCISE TO MODIFY:
Sentence: "${editingSentence.sentenceText}"
Correct Answer: "${getCorrectAnswerFromBlanks(editingSentence.blanks)}"
Answer Options: ${editingSentence.blanks[0]?.options.join(", ")}
Target Language: ${editingSentence.targetLanguage}
Base Language: ${editingSentence.baseLanguage}

IMPORTANT: Your modification should preserve the exercise format while applying the user's requested changes.

RULES:
1. Modify the sentence based on the user's instructions
2. Provide a new correct answer (the word that fills the blank)
3. Provide 3-4 distractor options (incorrect words similar to the correct answer)
4. Ensure the sentence remains natural and grammatically correct
5. Keep the same target and base languages
6. Maintain appropriate difficulty level

Output Format:
Respond ONLY with a valid JSON object:
{
  "sentence": "modified sentence with _____ blank",
  "correctAnswer": "correct word",
  "options": ["correct word", "distractor1", "distractor2", "distractor3"]
}`;

      const userContent = `
Target Language: ${editingSentence.targetLanguage}
Base Language: ${editingSentence.baseLanguage}
Domain: ${editingSentence.domain}
Proficiency Level: ${editingSentence.proficiencyLevel}
Difficulty: ${editingSentence.difficulty}

Modification Instructions: ${aiEditPrompt}

Please modify the exercise based on these instructions and return it in the exact JSON format specified. Remember to preserve the core meaning and context while applying the requested changes.`;

      const payload = {
        messages: [
          {
            role: "system",
            content: systemContent,
          },
          {
            role: "user",
            content: userContent,
          },
        ],
        temperature: 0.7,
        max_tokens: 1000,
        json_mode: true,
      };

      console.log("Sending AI edit request:", payload);

      const result = await callOpenAI(payload);
      console.log("AI edit response:", result);

      if (!result) {
        throw new Error("No response received from AI");
      }

      // Parse the response
      let parsedResponse: any;
      try {
        if (typeof result.response === 'object' && result.response !== null) {
          parsedResponse = result.response;
        } else if (typeof result.response === 'string') {
          let jsonString = result.response;
          const startIndex = jsonString.indexOf("{");
          const endIndex = jsonString.lastIndexOf("}") + 1;
          if (startIndex !== -1 && endIndex > startIndex) {
            jsonString = jsonString.slice(startIndex, endIndex).trim();
            parsedResponse = JSON.parse(jsonString);
          } else {
            throw new Error("No valid JSON found in response");
          }
        } else {
          throw new Error(`Unexpected response type: ${typeof result.response}`);
        }
      } catch (parseError: any) {
        console.error("Failed to parse AI response:", parseError);
        throw new Error(`Invalid response format from AI: ${parseError.message}`);
      }

      // Create preview
      const preview: FormattedPracticeSentence = {
        ...editingSentence,
        sentenceText: parsedResponse.sentence,
        blanks: [{
          correctAnswer: parsedResponse.correctAnswer,
          options: parsedResponse.options || [parsedResponse.correctAnswer],
          placeholder: "___"
        }]
      };

      setAIEditPreview(preview);
      console.log("AI edit preview created:", preview);

    } catch (err: any) {
      console.error("Error processing AI edit:", err);
      alert(`Failed to process AI edit: ${err.message || "Unknown error"}`);
    } finally {
      setIsProcessingAIEdit(false);
    }
  };

  const handleApplyAIEdit = async () => {
    if (!aiEditPreview || !editingSentence) return;

    try {
      if (updateAllVariations) {
        // Find all variations (exercises with the same target sentence text)
        const variations = existingPracticeSentences.filter(
          (ex) => ex.sentenceText === editingSentence.sentenceText
        );

        // Update each variation in the database
        await Promise.all(
          variations.map((variation) => {
            const updatedVariation: FormattedPracticeSentence = {
              ...variation,
              sentenceText: aiEditPreview.sentenceText,
              blanks: aiEditPreview.blanks,
            };
            return updatePracticeSentence(
              variation.sectionId,
              variation.sentenceId,
              updatedVariation
            );
          })
        );

        // Update local state for all variations
        setExistingPracticeSentences((prev) =>
          prev.map((sentence) => {
            if (sentence.sentenceText === editingSentence.sentenceText) {
              return {
                ...sentence,
                sentenceText: aiEditPreview.sentenceText,
                blanks: aiEditPreview.blanks,
              };
            }
            return sentence;
          })
        );

        handleCloseAIEdit();
        alert(`Successfully updated ${variations.length} variation(s) with AI changes!`);
      } else {
        // Update only the single exercise in the database
        await updatePracticeSentence(
          editingSentence.sectionId,
          editingSentence.sentenceId,
          aiEditPreview
        );

        // Update local state for single exercise
        setExistingPracticeSentences((prev) =>
          prev.map((sentence) =>
            sentence.sentenceId === editingSentence.sentenceId
              ? aiEditPreview
              : sentence
          )
        );

        handleCloseAIEdit();
        alert("Exercise updated successfully with AI changes!");
      }
    } catch (err: any) {
      console.error("Error applying AI edit:", err);
      alert(`Failed to apply AI edit: ${err.message || "Unknown error"}`);
    }
  };

  // ── Generate Missing Base Languages Handler ──
  const handleGenerateMissingBaseLanguages = async (sentenceText: string, variations: FormattedPracticeSentence[]) => {
    // Use EN variation as reference if available, otherwise use first variation
    const referenceVariation = variations.find(v => v.baseLanguage === 'EN') || variations[0];

    // Get target language and exclude it from expected base languages
    const targetLanguage = referenceVariation.targetLanguage;
    const expectedBaseLanguages = ALL_BASE_LANGUAGES.filter(lang => lang !== targetLanguage);

    // Get existing base languages
    const existingBaseLanguages = new Set(variations.map(v => v.baseLanguage));
    const missingBaseLanguages = expectedBaseLanguages.filter(lang => !existingBaseLanguages.has(lang));

    if (missingBaseLanguages.length === 0) {
      alert("This sentence already has all base language variations!");
      return;
    }

    const confirmGenerate = window.confirm(
      `Generate ${missingBaseLanguages.length} missing base language variation(s)?\n\n` +
      `Target Language: ${targetLanguage}\n` +
      `Missing: ${missingBaseLanguages.join(', ')}\n` +
      `Current: ${Array.from(existingBaseLanguages).join(', ')}\n\n` +
      `Reference: ${referenceVariation.baseLanguage}`
    );

    if (!confirmGenerate) return;

    // Mark this sentence as being processed
    setGeneratingMissingLanguages(prev => new Set(prev).add(sentenceText));

    try {
      const newVariations: FormattedPracticeSentence[] = [];

      // Generate each missing base language variation
      for (const targetBaseLanguage of missingBaseLanguages) {
        console.log(`Generating ${targetBaseLanguage} variation...`);

        const systemContent = `
You are an AI assistant specialized in translating fill-in-the-blank exercises for language learning.

REFERENCE EXERCISE (in ${referenceVariation.baseLanguage}):
Sentence: "${referenceVariation.sentenceText}"
Correct Answer: "${getCorrectAnswerFromBlanks(referenceVariation.blanks)}"
Answer Options: ${referenceVariation.blanks[0]?.options.join(", ")}
Target Language (the language being learned): ${referenceVariation.targetLanguage}

TASK:
Translate this fill-in-the-blank exercise to ${targetBaseLanguage} (the base/instruction language).
The sentence is in the TARGET language (${referenceVariation.targetLanguage}), which should NOT change.
Only translate the context/meaning to help ${targetBaseLanguage} speakers understand the exercise.

IMPORTANT:
- The sentence text stays in ${referenceVariation.targetLanguage}
- The correct answer stays in ${referenceVariation.targetLanguage}
- The options stay in ${referenceVariation.targetLanguage}
- This is the SAME exercise, just for ${targetBaseLanguage}-speaking learners

Output Format:
Respond ONLY with a valid JSON object:
{
  "sentence": "same sentence in ${referenceVariation.targetLanguage}",
  "correctAnswer": "same correct answer in ${referenceVariation.targetLanguage}",
  "options": ["same options in ${referenceVariation.targetLanguage}"]
}`;

        const userContent = `
Target Language: ${referenceVariation.targetLanguage}
Base Language for this variation: ${targetBaseLanguage}
Domain: ${referenceVariation.domain}
Proficiency Level: ${referenceVariation.proficiencyLevel}
Difficulty: ${referenceVariation.difficulty}

Please provide the exercise data for ${targetBaseLanguage}-speaking learners in the exact JSON format specified.`;

        const payload = {
          messages: [
            { role: "system", content: systemContent },
            { role: "user", content: userContent },
          ],
          temperature: 0.3,
          max_tokens: 1000,
          json_mode: true,
        };

        const result = await callOpenAI(payload);

        if (!result) {
          console.error(`Failed to get AI response for ${targetBaseLanguage}`);
          continue;
        }

        // Parse the response
        let parsedResponse: any;
        try {
          if (typeof result.response === 'object' && result.response !== null) {
            parsedResponse = result.response;
          } else if (typeof result.response === 'string') {
            let jsonString = result.response;
            const startIndex = jsonString.indexOf("{");
            const endIndex = jsonString.lastIndexOf("}") + 1;
            if (startIndex !== -1 && endIndex > startIndex) {
              jsonString = jsonString.slice(startIndex, endIndex).trim();
              parsedResponse = JSON.parse(jsonString);
            } else {
              throw new Error("No valid JSON found in response");
            }
          } else {
            throw new Error(`Unexpected response type: ${typeof result.response}`);
          }
        } catch (parseError: any) {
          console.error(`Failed to parse AI response for ${targetBaseLanguage}:`, parseError);
          continue;
        }

        // Create new variation
        const newVariation: FormattedPracticeSentence = {
          ...referenceVariation,
          sentenceId: generateUUID(),
          baseLanguage: targetBaseLanguage,
          sentenceText: parsedResponse.sentence,
          blanks: [{
            correctAnswer: parsedResponse.correctAnswer,
            options: parsedResponse.options || [parsedResponse.correctAnswer],
            placeholder: "___"
          }],
          createdAt: new Date().toISOString(),
        };

        newVariations.push(newVariation);
      }

      if (newVariations.length === 0) {
        alert("Failed to generate any variations. Please check the console for errors.");
        return;
      }

      // Save all new variations to the database
      await Promise.all(
        newVariations.map((variation) => createPracticeSentence(variation))
      );

      // Update local state
      setExistingPracticeSentences((prev) => [...newVariations, ...prev]);

      alert(`Successfully generated ${newVariations.length} new base language variation(s)!`);

    } catch (err: any) {
      console.error("Error generating missing base languages:", err);
      alert(`Failed to generate missing variations: ${err.message || "Unknown error"}`);
    } finally {
      // Remove from generating set
      setGeneratingMissingLanguages(prev => {
        const next = new Set(prev);
        next.delete(sentenceText);
        return next;
      });
    }
  };

  // ── PracticeSentenceCarousel Component ──
  const PracticeSentenceCarousel: React.FC<{
    variations: FormattedPracticeSentence[];
    sentenceText: string;
    onEdit: (sentence: FormattedPracticeSentence) => void;
    onDelete: (sentence: FormattedPracticeSentence) => void;
    onDeleteAll: (sentenceText: string, sectionId: string) => void;
    onGenerateMissing: (sentenceText: string, variations: FormattedPracticeSentence[]) => void;
    isGenerating: boolean;
  }> = ({ variations, sentenceText, onEdit, onDelete, onDeleteAll, onGenerateMissing, isGenerating }) => {
    // Find initial index: prioritize "EN"
    const initialIndex = useMemo(() => {
      if (!variations || variations.length === 0) return 0;
      const enIndex = variations.findIndex((v) => v.baseLanguage === "EN");
      return enIndex !== -1 ? enIndex : 0;
    }, [variations]);

    const [currentIndex, setCurrentIndex] = useState<number>(initialIndex);

    // Reset index when variations change
    useEffect(() => {
      setCurrentIndex(initialIndex);
    }, [initialIndex]);

    const handleCarouselChange = (direction: "prev" | "next") => {
      if (!variations || variations.length <= 1) return;
      setCurrentIndex((prev) => {
        const newIndex =
          direction === "next"
            ? (prev + 1) % variations.length
            : (prev - 1 + variations.length) % variations.length;
        return newIndex;
      });
    };

    if (!variations || variations.length === 0) {
      return <div className={styles.carousel}>No variations.</div>;
    }

    const currentVariation =
      currentIndex < variations.length ? variations[currentIndex] : null;

    if (!currentVariation) {
      return <div className={styles.carousel}>Error loading variation.</div>;
    }

    // Helper function to parse distractors/options - handles arrays, JSON strings, and CSV strings
    const parseOptions = (raw: any): string[] => {
      if (!raw) return [];

      // If it's already an array, just filter and return
      if (Array.isArray(raw)) {
        return raw.filter(item => item && typeof item === 'string' && item.trim());
      }

      // If it's not a string, return empty
      if (typeof raw !== 'string') return [];

      // If empty string, return empty
      if (raw.trim() === '') return [];

      // Try parsing as JSON array
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          return parsed.filter(item => item && typeof item === 'string' && item.trim());
        }
      } catch {
        // Not valid JSON, continue to CSV parsing
      }

      // Parse as comma-separated string
      return raw
        .split(',')
        .map(item => item.trim())
        .filter(item => item.length > 0);
    };

    const options = currentVariation.blanks[0]?.options || [];
    const parsedOptions = parseOptions(options);

    // Calculate missing base languages (exclude target language)
    const targetLanguage = currentVariation.targetLanguage;
    const expectedBaseLanguages = ALL_BASE_LANGUAGES.filter(lang => lang !== targetLanguage);
    const existingBaseLanguages = new Set(variations.map(v => v.baseLanguage));
    const missingBaseLanguages = expectedBaseLanguages.filter(lang => !existingBaseLanguages.has(lang));
    const hasMissingLanguages = missingBaseLanguages.length > 0;

    return (
      <div className={styles.carousel}>
        <button
          onClick={() => handleCarouselChange("prev")}
          className={styles.carouselButton}
          disabled={variations.length <= 1}
          aria-label="Previous variation"
        >
          ←
        </button>
        <div className={styles.carouselContent}>
          <div style={{ marginBottom: "10px" }}>
            <strong>Base Language ({currentVariation.baseLanguage}):</strong>
            <div style={{ marginTop: "5px", fontSize: "16px" }}>
              {renderSentenceWithBlanks(currentVariation.sentenceText)}
            </div>
            {hasMissingLanguages && (
              <div style={{ marginTop: "5px", fontSize: "12px", color: "#ff9800" }}>
                Missing: {missingBaseLanguages.join(', ')} ({variations.length}/{expectedBaseLanguages.length} base languages)
              </div>
            )}
          </div>
          <div style={{ fontSize: "0.85em", color: "#666" }}>
            <div><strong>Domain:</strong> {currentVariation.domain}</div>
            <div><strong>Proficiency:</strong> {currentVariation.proficiencyLevel}</div>
            <div><strong>Difficulty:</strong> {currentVariation.difficulty}</div>
            <div style={{ marginTop: "8px" }}>
              <strong>Correct Answer:</strong> {getCorrectAnswerFromBlanks(currentVariation.blanks)}
            </div>
            {parsedOptions.length > 0 && (
              <div style={{ marginTop: "4px" }}>
                <strong style={{ color: "#1565c0" }}>Options:</strong>{" "}
                <span style={{ color: "#555" }}>{parsedOptions.join(", ")}</span>
              </div>
            )}
          </div>
          <div style={{ marginTop: "10px", display: "flex", gap: "5px", flexWrap: "wrap" }}>
            <button
              onClick={() => onEdit(currentVariation)}
              className={`${styles.button} ${styles.editButton}`}
            >
              Edit
            </button>
            <button
              onClick={() => onDelete(currentVariation)}
              className={`${styles.button} ${styles.deleteButton}`}
            >
              Delete
            </button>
            {variations.length > 1 && (
              <button
                onClick={() => onDeleteAll(currentVariation.sentenceText, currentVariation.sectionId)}
                className={`${styles.button} ${styles.deleteButton}`}
                style={{ backgroundColor: '#d32f2f', fontWeight: 'bold' }}
                title={`Delete all ${variations.length} variation(s) of this sentence`}
              >
                Delete All ({variations.length})
              </button>
            )}
            {hasMissingLanguages && (
              <button
                onClick={() => onGenerateMissing(sentenceText, variations)}
                className={`${styles.button}`}
                style={{ backgroundColor: '#4caf50', fontWeight: 'bold' }}
                disabled={isGenerating}
                title={`Generate ${missingBaseLanguages.length} missing base language(s): ${missingBaseLanguages.join(', ')}`}
              >
                {isGenerating ? 'Generating...' : `Generate Missing (${missingBaseLanguages.length})`}
              </button>
            )}
          </div>
        </div>
        <button
          onClick={() => handleCarouselChange("next")}
          className={styles.carouselButton}
          disabled={variations.length <= 1}
          aria-label="Next variation"
        >
          →
        </button>
      </div>
    );
  };

  return (
    <div className={styles.container}>
      <BackButton />
      <h1 className={styles.title}>Fill In The Blank Manager</h1>

      {/* Controls */}
      <LanguageControlsGrid
        sourceLanguage={sourceLanguage}
        section={section}
        domain={domain}
        proficiencyLevel={proficiencyLevel}
        difficulty={difficulty}
        isGenerating={generatingAi}
        onSourceLanguageChange={handleLanguageChange}
        onSectionChange={handleSectionChange}
        onDomainChange={handleDomainChange}
        onProficiencyChange={handleProfChange}
        onDifficultyChange={handleDiffChange}
        onSectionsLoaded={handleSectionsLoaded}
        sectionLabel="Section:"
        error={error}
      />
      
      {/* Domain override for FillInTheBlank (extended domains) */}
      <div className={styles.domainOverride}>
        <div className={styles.formControl}>
          <label htmlFor="domainOverride">Domain:</label>
          <select
            id="domainOverride"
            value={domain}
            onChange={handleDomainChange}
            disabled={generatingAi}
          >
            {EXTENDED_DOMAINS.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
        </div>
      </div>

      {error && <div className={styles.error}>{error}</div>}

      {/* Vocabulary table */}
      {section && vocabularyWords.length > 0 && (
        <div className={styles.sectionContainer}>
          <h2>Select Vocabulary Words</h2>
          <table className={styles.vocabTable}>
            <thead>
              <tr>
                <th>Select</th>
                <th>Word</th>
              </tr>
            </thead>
            <tbody>
              {vocabularyWords.map((w, i) => (
                <tr key={i}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedWordsBucket.has(w)}
                      onChange={() => toggleWord(w)}
                    />
                  </td>
                  <td>{w}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Generate button */}
      {selectedWordsBucket.size > 0 && (
        <button
          onClick={handleGenerateData}
          disabled={generatingAi}
        >
          {generatingAi
            ? "Generating..."
            : `Generate Fill-in-the-Blanks (${selectedWordsBucket.size})`}
        </button>
      )}
      {aiError && <div className={styles.error}>{aiError}</div>}

      {/* AI Preview */}
      {aiGeneratedContent && (
        <div className={styles.sectionContainer}>
          <h2>AI Generated Sentences</h2>
          {aiGeneratedContent.map((item) => (
            <div
              key={item.sentenceId}
              className={styles.aiQuestionItem}
            >
              <p>
                <strong>Sentence:</strong> {renderSentenceWithBlanks(item.sentenceText)}
              </p>
              <p>
                <strong>Answer:</strong> {getCorrectAnswerFromBlanks(item.blanks)}
              </p>
              <p>
                <strong>Options:</strong> {item.blanks[0].options.join(", ")}
              </p>
            </div>
          ))}
          <button
            onClick={handleSaveAll}
            disabled={isSavingAi}
          >
            {isSavingAi ? "Saving..." : "Save All AI Generated"}
          </button>
        </div>
      )}

      {/* Existing sentences */}
      {section && (
        <div className={styles.sectionContainer}>
          <h2 className={styles.subHeading}>
            Existing Practice Sentences for "{section}"
          </h2>

          {/* Base Language Filter */}
          {existingPracticeSentences.length > 0 && (
            <div className={styles.filterContainer}>
              <div className={styles.formControl}>
                <label htmlFor="baseLanguageFilter" className={styles.label}>
                  Filter by Base Language:
                </label>
                <select
                  id="baseLanguageFilter"
                  value={baseLanguageFilter}
                  onChange={(e) => setBaseLanguageFilter(e.target.value)}
                  className={styles.input}
                >
                  <option value="All">All Base Languages</option>
                  {uniqueBaseLanguages.map((lang) => (
                    <option key={lang} value={lang}>
                      {lang}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {loadingExisting && (
            <p className={styles.infoMessage}>Loading existing sentences...</p>
          )}
          {!loadingExisting &&
            existingPracticeSentences.length === 0 && (
              <p className={styles.infoMessage}>
                No existing practice sentences found for this section.
              </p>
            )}
          {!loadingExisting &&
            filteredExercises.length === 0 &&
            existingPracticeSentences.length > 0 && (
              <p className={styles.infoMessage}>
                No exercises found for the selected base language filter.
              </p>
            )}
          {!loadingExisting &&
            groupedSentences.length > 0 && (
              <>
                {/* Bulk Actions */}
                <div style={{ marginBottom: '15px', display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <button
                    onClick={handleOpenBulkAIEdit}
                    disabled={selectedSentenceTexts.size === 0}
                    className={`${styles.button}`}
                    style={{ backgroundColor: '#4a90e2' }}
                  >
                    🤖 Bulk AI Edit ({selectedSentenceTexts.size})
                  </button>
                  {selectedSentenceTexts.size > 0 && (
                    <button
                      onClick={() => setSelectedSentenceTexts(new Set())}
                      className={styles.button}
                      style={{ backgroundColor: '#757575' }}
                    >
                      Clear Selection
                    </button>
                  )}
                </div>

                <div className={styles.tableContainer}>
                  <table className={styles.dataTable}>
                    <thead>
                      <tr>
                        <th style={{ width: '40px' }}>
                          <input
                            type="checkbox"
                            checked={selectedSentenceTexts.size === groupedSentences.length && groupedSentences.length > 0}
                            onChange={toggleSelectAll}
                            title="Select/Deselect all"
                          />
                        </th>
                        <th>Target Sentence ({groupedSentences[0]?.targetLanguage || 'Target'})</th>
                        <th>Base Language Variations</th>
                      </tr>
                    </thead>
                    <tbody>
                      {groupedSentences.map((group, index) => (
                        <tr key={`group-${index}`}>
                          <td style={{ textAlign: 'center' }}>
                            <input
                              type="checkbox"
                              checked={selectedSentenceTexts.has(group.sentenceText)}
                              onChange={() => toggleSentenceSelection(group.sentenceText)}
                            />
                          </td>
                          <td style={{ verticalAlign: "top", maxWidth: "400px" }}>
                            <div style={{ fontWeight: "bold", marginBottom: "5px" }}>
                              {renderSentenceWithBlanks(group.sentenceText)}
                            </div>
                            <div style={{ fontSize: "0.85em", color: "#666" }}>
                              {group.variations.length} variation{group.variations.length !== 1 ? 's' : ''}
                            </div>
                          </td>
                          <td>
                            <PracticeSentenceCarousel
                              variations={group.variations}
                              sentenceText={group.sentenceText}
                              onEdit={handleOpenAIEdit}
                              onDelete={(sentence) => handleDelete(sentence.sectionId, sentence.sentenceId)}
                              onDeleteAll={handleDeleteAll}
                              onGenerateMissing={handleGenerateMissingBaseLanguages}
                              isGenerating={generatingMissingLanguages.has(group.sentenceText)}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
        </div>
      )}

      {/* ── AI Editing Modal ── */}
      {showAIEditModal && editingSentence && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent} style={{ maxWidth: '700px' }}>
            <h2>🤖 AI Exercise Editor</h2>

            {/* Current Exercise Display */}
            <div className={styles.modalSection} style={{
              backgroundColor: '#e3f2fd',
              border: '2px solid #2196f3',
              borderRadius: '6px',
              padding: '12px 15px',
              marginBottom: '15px'
            }}>
              <h4 style={{ margin: '0 0 8px 0', color: '#1565c0', fontSize: '14px' }}>
                📝 Current Exercise
              </h4>
              <div style={{ marginBottom: '8px' }}>
                <strong>Sentence:</strong> {renderSentenceWithBlanks(editingSentence.sentenceText)}
              </div>
              <div style={{ marginBottom: '8px' }}>
                <strong>Correct Answer:</strong> {getCorrectAnswerFromBlanks(editingSentence.blanks)}
              </div>
              <div style={{ marginBottom: '8px' }}>
                <strong>Options:</strong> {editingSentence.blanks[0]?.options.join(", ")}
              </div>
              <div style={{ fontSize: '13px', color: '#555', marginTop: '8px' }}>
                <span style={{ marginRight: '15px' }}>
                  <strong>Language:</strong> {editingSentence.targetLanguage} → {editingSentence.baseLanguage}
                </span>
                <span style={{ marginRight: '15px' }}>
                  <strong>Domain:</strong> {editingSentence.domain}
                </span>
                <span style={{ marginRight: '15px' }}>
                  <strong>Level:</strong> {editingSentence.proficiencyLevel}
                </span>
                <span>
                  <strong>Difficulty:</strong> {editingSentence.difficulty}
                </span>
              </div>
            </div>

            <div className={styles.modalSection}>
              <p style={{ marginBottom: '15px', color: '#666' }}>
                Describe how you want to modify this exercise. The AI will generate a new version based on your instructions.
              </p>

              <div style={{ marginBottom: '20px' }}>
                <h4>Example Instructions:</h4>
                <ul style={{ color: '#888', fontSize: '14px', lineHeight: '1.6' }}>
                  <li>"Make the sentence more formal"</li>
                  <li>"Change it to past tense"</li>
                  <li>"Make it a question instead"</li>
                  <li>"Simplify for beginner level"</li>
                  <li>"Add more context about the location"</li>
                  <li>"Change the topic to food"</li>
                </ul>
              </div>

              <label className={styles.label}>
                Modification Instructions:
                <textarea
                  value={aiEditPrompt}
                  onChange={(e) => setAIEditPrompt(e.target.value)}
                  placeholder="Describe how you want to modify this exercise..."
                  className={styles.input}
                  rows={4}
                  disabled={isProcessingAIEdit}
                  style={{ width: '100%', resize: 'vertical', marginTop: '8px' }}
                />
              </label>
            </div>

            {/* AI Edit Preview */}
            {aiEditPreview && (() => {
              // Calculate number of variations for this sentence
              const variationCount = existingPracticeSentences.filter(
                (ex) => ex.sentenceText === editingSentence.sentenceText
              ).length;

              return (
                <div className={styles.modalSection} style={{
                  backgroundColor: '#f0f8ff',
                  padding: '15px',
                  borderRadius: '8px',
                  marginTop: '15px'
                }}>
                  <h3 style={{ marginTop: '0', color: '#1565c0' }}>Preview Modified Exercise:</h3>

                  <div style={{ marginBottom: '12px', padding: '12px', backgroundColor: '#e8f5e9', borderRadius: '6px' }}>
                    <strong style={{ fontSize: '15px' }}>New Sentence:</strong>
                    <div style={{ marginTop: '8px', color: '#2a7f2a', fontWeight: 'bold', fontSize: '16px' }}>
                      {renderSentenceWithBlanks(aiEditPreview.sentenceText)}
                    </div>
                  </div>

                  <div style={{ marginBottom: '12px', padding: '12px', backgroundColor: '#fff3e0', borderRadius: '6px' }}>
                    <div style={{ marginBottom: '8px' }}>
                      <strong>Correct Answer:</strong> {getCorrectAnswerFromBlanks(aiEditPreview.blanks)}
                    </div>
                    <div>
                      <strong>Answer Options:</strong> {aiEditPreview.blanks[0]?.options.join(", ")}
                    </div>
                  </div>

                  {/* Update All Variations Option */}
                  {variationCount > 1 && (
                    <div style={{ marginTop: '15px', padding: '12px', backgroundColor: '#e1f5fe', borderRadius: '6px' }}>
                      <h4 style={{ marginTop: '0', marginBottom: '10px', color: '#01579b' }}>
                        Apply Changes To:
                      </h4>
                      <div style={{ fontSize: '14px', color: '#555', marginBottom: '12px' }}>
                        This sentence has <strong>{variationCount} base language variation(s)</strong>.
                        Choose how to apply these changes:
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', padding: '8px', backgroundColor: 'white', borderRadius: '4px', border: updateAllVariations ? '2px solid #2196f3' : '2px solid #ddd' }}>
                          <input
                            type="radio"
                            name="updateScope"
                            checked={updateAllVariations}
                            onChange={() => setUpdateAllVariations(true)}
                            style={{ marginRight: '10px' }}
                          />
                          <div>
                            <strong>Update All {variationCount} Variations</strong>
                            <div style={{ fontSize: '13px', color: '#666', marginTop: '2px' }}>
                              Apply these changes to all base language versions of this sentence
                            </div>
                          </div>
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', padding: '8px', backgroundColor: 'white', borderRadius: '4px', border: !updateAllVariations ? '2px solid #2196f3' : '2px solid #ddd' }}>
                          <input
                            type="radio"
                            name="updateScope"
                            checked={!updateAllVariations}
                            onChange={() => setUpdateAllVariations(false)}
                            style={{ marginRight: '10px' }}
                          />
                          <div>
                            <strong>Update Only Current Exercise ({editingSentence.baseLanguage})</strong>
                            <div style={{ fontSize: '13px', color: '#666', marginTop: '2px' }}>
                              Apply changes only to this specific variation
                            </div>
                          </div>
                        </label>
                      </div>
                    </div>
                  )}

                  <div style={{ marginTop: '15px', padding: '10px', backgroundColor: '#fff3cd', borderRadius: '4px', fontSize: '14px' }}>
                    <strong>Note:</strong> Click "Apply Changes" to save this modified exercise to the database, or "Regenerate" to try again with different instructions.
                  </div>
                </div>
              );
            })()}

            {/* Modal Actions */}
            <div className={styles.modalActions}>
              {!aiEditPreview ? (
                <>
                  <button
                    onClick={handleProcessAIEdit}
                    className={`${styles.button} ${styles.saveButton}`}
                    disabled={isProcessingAIEdit || !aiEditPrompt.trim()}
                    style={{ backgroundColor: '#4a90e2' }}
                  >
                    {isProcessingAIEdit ? "Generating..." : "Generate Preview"}
                  </button>
                  <button
                    onClick={handleCloseAIEdit}
                    className={styles.button}
                    disabled={isProcessingAIEdit}
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={handleApplyAIEdit}
                    className={`${styles.button} ${styles.saveButton}`}
                    style={{ backgroundColor: '#4caf50' }}
                  >
                    Apply Changes
                  </button>
                  <button
                    onClick={() => {
                      setAIEditPreview(null);
                      setAIEditPrompt("");
                    }}
                    className={`${styles.button}`}
                    style={{ backgroundColor: '#ff9800' }}
                  >
                    Regenerate
                  </button>
                  <button
                    onClick={handleCloseAIEdit}
                    className={styles.button}
                  >
                    Cancel
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Bulk AI Editing Modal ── */}
      {showBulkAIEditModal && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent} style={{ maxWidth: '900px', maxHeight: '90vh', overflowY: 'auto' }}>
            <h2>🤖 Bulk AI Exercise Editor</h2>

            <div className={styles.modalSection} style={{
              backgroundColor: '#e3f2fd',
              border: '2px solid #2196f3',
              borderRadius: '6px',
              padding: '12px 15px',
              marginBottom: '15px'
            }}>
              <h4 style={{ margin: '0 0 8px 0', color: '#1565c0', fontSize: '14px' }}>
                📝 Selected Sentences ({selectedSentenceTexts.size})
              </h4>
              <div style={{ maxHeight: '150px', overflowY: 'auto', fontSize: '14px' }}>
                {Array.from(selectedSentenceTexts).map((text, idx) => (
                  <div key={idx} style={{ marginBottom: '5px', paddingLeft: '10px' }}>
                    • {renderSentenceWithBlanks(text)}
                  </div>
                ))}
              </div>
            </div>

            <div className={styles.modalSection}>
              <p style={{ marginBottom: '15px', color: '#666' }}>
                Describe how you want to modify ALL selected exercises. The AI will apply the same modification to each sentence.
              </p>

              <div style={{ marginBottom: '20px' }}>
                <h4>Example Instructions:</h4>
                <ul style={{ color: '#888', fontSize: '14px', lineHeight: '1.6' }}>
                  <li>"Make all sentences more formal"</li>
                  <li>"Change all to past tense"</li>
                  <li>"Make all sentences into questions"</li>
                  <li>"Simplify all for beginner level"</li>
                  <li>"Add more context about location to all"</li>
                </ul>
              </div>

              <label className={styles.label}>
                Modification Instructions:
                <textarea
                  value={bulkAIEditPrompt}
                  onChange={(e) => setBulkAIEditPrompt(e.target.value)}
                  placeholder="Describe how you want to modify all selected exercises..."
                  className={styles.input}
                  rows={4}
                  disabled={isProcessingBulkAIEdit}
                  style={{ width: '100%', resize: 'vertical', marginTop: '8px' }}
                />
              </label>
            </div>

            {/* Bulk AI Edit Previews */}
            {bulkAIEditPreviews.size > 0 && (
              <div className={styles.modalSection} style={{
                backgroundColor: '#f0f8ff',
                padding: '15px',
                borderRadius: '8px',
                marginTop: '15px',
                maxHeight: '400px',
                overflowY: 'auto'
              }}>
                <h3 style={{ marginTop: '0', color: '#1565c0' }}>
                  Preview Modified Exercises ({bulkAIEditPreviews.size} of {selectedSentenceTexts.size}):
                </h3>

                {Array.from(bulkAIEditPreviews.entries()).map(([originalText, preview], idx) => (
                  <div key={idx} style={{
                    marginBottom: '15px',
                    padding: '12px',
                    backgroundColor: 'white',
                    borderRadius: '6px',
                    border: '1px solid #ddd'
                  }}>
                    <div style={{ marginBottom: '8px', fontSize: '13px', color: '#999' }}>
                      <strong>Original:</strong> {renderSentenceWithBlanks(originalText)}
                    </div>
                    <div style={{ marginBottom: '8px', padding: '10px', backgroundColor: '#e8f5e9', borderRadius: '4px' }}>
                      <strong style={{ fontSize: '14px', color: '#2a7f2a' }}>New Sentence:</strong>
                      <div style={{ marginTop: '5px', fontWeight: 'bold', color: '#2a7f2a' }}>
                        {renderSentenceWithBlanks(preview.sentenceText)}
                      </div>
                    </div>
                    <div style={{ fontSize: '13px' }}>
                      <strong>Correct Answer:</strong> {getCorrectAnswerFromBlanks(preview.blanks)}
                      <br />
                      <strong>Options:</strong> {preview.blanks[0]?.options.join(", ")}
                    </div>
                  </div>
                ))}

                <div style={{ marginTop: '15px', padding: '10px', backgroundColor: '#fff3cd', borderRadius: '4px', fontSize: '14px' }}>
                  <strong>Note:</strong> These changes will be applied to ALL base language variations of each sentence.
                  Click "Apply All Changes" to save, or "Regenerate" to try again.
                </div>
              </div>
            )}

            {/* Modal Actions */}
            <div className={styles.modalActions}>
              {bulkAIEditPreviews.size === 0 ? (
                <>
                  <button
                    onClick={handleProcessBulkAIEdit}
                    className={`${styles.button} ${styles.saveButton}`}
                    disabled={isProcessingBulkAIEdit || !bulkAIEditPrompt.trim()}
                    style={{ backgroundColor: '#4a90e2' }}
                  >
                    {isProcessingBulkAIEdit ? `Processing (${bulkAIEditPreviews.size}/${selectedSentenceTexts.size})...` : "Generate Previews"}
                  </button>
                  <button
                    onClick={handleCloseBulkAIEdit}
                    className={styles.button}
                    disabled={isProcessingBulkAIEdit}
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={handleApplyBulkAIEdit}
                    className={`${styles.button} ${styles.saveButton}`}
                    style={{ backgroundColor: '#4caf50' }}
                  >
                    Apply All Changes ({bulkAIEditPreviews.size})
                  </button>
                  <button
                    onClick={() => {
                      setBulkAIEditPreviews(new Map());
                      setBulkAIEditPrompt("");
                    }}
                    className={`${styles.button}`}
                    style={{ backgroundColor: '#ff9800' }}
                  >
                    Regenerate
                  </button>
                  <button
                    onClick={handleCloseBulkAIEdit}
                    className={styles.button}
                  >
                    Cancel
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FillInTheBlankManager;
